import { useCallback, useEffect, useRef, useState } from 'react';
import { previewReminderTemplate } from '../../../../api/collections';
import { buildTemplatePreviewPayload } from '../utils/reminderTemplates';

/** Espera entre teclas antes de pedir la vista previa (billing la limita por usuario). */
export const TEMPLATE_PREVIEW_DEBOUNCE_MS = 450;

const EMPTY = { pending: false, data: null, error: null };

/**
 * Vista previa en vivo de una plantilla (POST billing/collections/templates/preview).
 *
 * Billing arma el mensaje real (variables, detalle de notas, montos): el front no lo
 * reconstruye. Para no gastar el límite de pedidos de billing, se pide recién cuando el
 * operador deja de escribir `delay` ms. Cada cambio del borrador invalida lo que esté en
 * vuelo: una respuesta que llega tarde para un texto que ya cambió se descarta, así la vista
 * previa nunca muestra un texto viejo como si fuera el actual. Mientras llega la nueva se
 * conserva la última buena (`data`) con `pending: true`; si la nueva falla, también (con
 * `error`), para mostrarla atenuada.
 *
 * Cambiar de fuente o de comercio no es editar el mismo mensaje: lo que había se descarta
 * en el acto, así un error (p. ej. un comercio sin notas) nunca queda al lado del mensaje de
 * otro comercio o de los datos de ejemplo.
 *
 * No pide nada si el cuerpo está vacío o si falta el comercio con `source: 'company'`.
 *
 * @param {{ channel: 'email'|'whatsapp', subject?: string, body: string,
 *   source: 'sample'|'company', companyId?: string, enabled?: boolean, delay?: number }} params
 * @returns {{ pending: boolean, data: object|null, error: unknown, refresh: () => void,
 *   idle: 'empty_body'|'missing_company'|null }}
 */
export default function useTemplatePreview({
  channel,
  subject,
  body,
  source,
  companyId,
  enabled = true,
  delay = TEMPLATE_PREVIEW_DEBOUNCE_MS,
}) {
  const [state, setState] = useState(EMPTY);
  const requestSeq = useRef(0);

  let idle = null;
  if (!String(body ?? '').trim()) idle = 'empty_body';
  else if (source === 'company' && !companyId) idle = 'missing_company';

  // De qué mensaje es la vista previa: canal, fuente y comercio (el borrador no cuenta).
  const scope = `${channel}|${source}|${source === 'company' ? companyId ?? '' : ''}`;
  const lastScope = useRef(scope);

  const run = useCallback(
    async (seq) => {
      const payload = buildTemplatePreviewPayload({ channel, subject, body, source, companyId });
      try {
        const { data } = await previewReminderTemplate(payload);
        if (seq !== requestSeq.current) return;
        setState({ pending: false, data, error: null });
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setState((s) => ({ pending: false, data: s.data, error: err }));
      }
    },
    [channel, subject, body, source, companyId],
  );

  useEffect(() => {
    // Todo cambio del borrador deja vieja cualquier respuesta en vuelo.
    const seq = ++requestSeq.current;
    const sameScope = lastScope.current === scope;
    lastScope.current = scope;
    if (!enabled || idle) {
      setState((s) => {
        if (!sameScope) return EMPTY;
        return s.pending ? { ...s, pending: false } : s;
      });
      return undefined;
    }
    setState((s) => {
      if (!sameScope) return { ...EMPTY, pending: true };
      return s.pending ? s : { ...s, pending: true };
    });
    const timer = setTimeout(() => run(seq), delay);
    return () => clearTimeout(timer);
  }, [enabled, idle, run, delay, scope]);

  // Al desmontar, nada de lo que esté en vuelo escribe.
  useEffect(
    () => () => {
      requestSeq.current += 1;
    },
    [],
  );

  const refresh = useCallback(() => {
    if (!enabled || idle) return;
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, pending: true }));
    run(seq);
  }, [enabled, idle, run]);

  return { ...state, idle, refresh };
}
